import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";

const sanitizeText = (txt) =>
  (txt || "")
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "")
    .trim();

const truncate = (str, max = 40) =>
  str && str.length > max ? str.substring(0, max - 3) + "..." : str || "";

const filteredItems = (order) =>
  order?.items?.filter(
    (item) =>
      item.name && !["discount", "shipping"].includes(item.name.toLowerCase())
  ) || [];

const formatFileSizeFriendly = (fileSize) => {
  if (!fileSize && fileSize !== 0) return "N/A";
  if (typeof fileSize === "string") return fileSize;
  const mb = Number(fileSize) / 1024 / 1024;
  if (isNaN(mb)) return "N/A";
  return mb.toFixed(2) + " MB";
};

const EmbroideryOrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const ordersList = location.state?.orders || [];
  const [order, setOrder] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [designLoading, setDesignLoading] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const userEmail = localStorage.getItem("email");
  const isEmbroideryUser = [
    "embroidery@txmaya.com",
    "mhmtmnctl20@gmail.com",
  ].includes(userEmail);

  const orderLevelDesigns = (designs || []).filter((d) => {
    if (!d) return false;
    const pos = (d.position || "").toString().toLowerCase();
    return (
      pos === "order" ||
      !d.line_item_key ||
      d.line_item_key === null ||
      pos === ""
    );
  });

  // Hover preview state for image zoom
  const [hoverPreview, setHoverPreview] = useState({
    visible: false,
    src: null,
    x: 0,
    y: 0,
  });
  const hoverPreviewSize = 250;

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  const prevOrderId = useMemo(() => {
    const currentIndex = ordersList.findIndex(
      (o) => String(o.orderId) === String(orderId)
    );
    return currentIndex > 0 ? ordersList[currentIndex - 1]?.orderId : null;
  }, [ordersList, orderId]);

  const nextOrderId = useMemo(() => {
    const currentIndex = ordersList.findIndex(
      (o) => String(o.orderId) === String(orderId)
    );
    return currentIndex >= 0 && currentIndex < ordersList.length - 1
      ? ordersList[currentIndex + 1]?.orderId
      : null;
  }, [ordersList, orderId]);

  const currentOrder = useMemo(() => {
    return ordersList.find((o) => String(o.orderId) === String(orderId));
  }, [ordersList, orderId]);

  const getOptionValue = (item, keys = []) => {
    if (!item || !Array.isArray(item.options)) return null;
    for (const opt of item.options) {
      if (!opt || !opt.name) continue;
      const name = String(opt.name).toLowerCase().trim();
      for (const k of keys) {
        if (name === k.toLowerCase()) {
          return opt.value ?? null;
        }
      }
    }
    return null;
  };

  const fetchOrder = async () => {
    setLoading(true);
    const source = currentOrder?.source || "maya";

    try {
      const resp = await axios.get(
        `${settings.apiURL}/embroidery_get_order_details.php?orderId=${orderId}&source=${source}`,
        { headers: authHeader() }
      );
      if (resp.data.success) {
        setOrder(resp.data.order);
        setInternalNote(resp.data.order?.internalNotes || "");
        // top-level unassigned designs (from server)
        setDesigns(resp.data.designs || []);
      } else {
        toast("Order details could not be loaded", "error");
      }
    } catch (err) {
      console.error("Failed to fetch order details", err);
      toast("Order details could not be loaded", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchDesigns = async (orderNumber) => {
    setDesignLoading(true);
    const source = currentOrder?.source || "maya";
    const apiURL =
      source === "customzon" ? settings.customzonApiURL : settings.apiURL;

    try {
      const resp = await axios.get(
        `${apiURL}/embroidery_get_order_designs.php?orderNumber=${orderNumber}`,
        { headers: authHeader() }
      );

      if (resp.data.success) {
        setDesigns(resp.data.designs || []);
      } else {
        setDesigns([]);
      }
    } catch (err) {
      console.error("Failed to fetch designs", err);
      setDesigns([]);
    } finally {
      setDesignLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (order?.orderNumber) {
      // backend now attaches uploaded_designs per item; still refresh global list
      fetchDesigns(order.orderNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  /**
   * Build a deterministic mapping of designs -> item index.
   * Prefer server-provided item.uploaded_designs when available (authoritative).
   * Otherwise, map using:
   * 1) explicit index token in filename: __<index>__
   * 2) line_item_key exact match
   * 3) sku exact match only when sku is unique across items
   * 4) sanitized item name match only when unique across items
   *
   * This ensures each design is assigned to at most one item.
   */
  const mappedDesignsByItem = useMemo(() => {
    if (!order) return [];
    const items = filteredItems(order);
    if (!items || items.length === 0) return [];

    // If backend already provided uploaded_designs per item, use them directly (but normalize)
    const backendProvided = items.every((it) =>
      Array.isArray(it.uploaded_designs)
    );
    if (
      backendProvided &&
      items.some((it) => (it.uploaded_designs || []).length > 0)
    ) {
      // Ensure file_size exists on each design: try to enrich from top-level designs state
      const designsIndex = (designs || []).reduce((acc, d) => {
        if (d && d.file_id) acc[d.file_id] = d;
        return acc;
      }, {});
      return items.map((it) => {
        const list = (it.uploaded_designs || []).map((d) => {
          const file = designsIndex[d.file_id] || {};
          return { ...file, ...d }; // uploaded entry overrides where available
        });
        return list;
      });
    }

    // Otherwise compute mapping from top-level designs array
    const listDesigns = designs || [];
    const byItem = items.map(() => []);
    const assignedFileIds = new Set();

    // helpers
    const parseIndexFromFileName = (fn) => {
      if (!fn) return null;
      const m = fn.match(/__([0-9]+)__/);
      if (m && m[1]) {
        const v = parseInt(m[1], 10);
        return Number.isNaN(v) ? null : v;
      }
      return null;
    };

    // precompute counts for sku and sanitized names
    const nameCounts = {};
    const skuCounts = {};
    items.forEach((it) => {
      const s = sanitizeText(it.name || "");
      if (s) nameCounts[s] = (nameCounts[s] || 0) + 1;
      const sku = (it.sku || "").toString().toLowerCase();
      if (sku) skuCounts[sku] = (skuCounts[sku] || 0) + 1;
    });

    // 1) explicit index token assignments
    for (const d of listDesigns) {
      if (!d || !d.file_name) continue;
      const fn = (d.file_name || "").toLowerCase();
      const parsedIdx = parseIndexFromFileName(fn);
      if (parsedIdx !== null && parsedIdx >= 0 && parsedIdx < items.length) {
        const fileId = d.file_id;
        if (!assignedFileIds.has(fileId)) {
          byItem[parsedIdx].push(d);
          assignedFileIds.add(fileId);
        }
      }
    }

    // 2) strict matches by line_item_key or sku (sku only if unique)
    for (let idx = 0; idx < items.length; idx++) {
      const item = items[idx];
      const itemLine = item.lineItemKey
        ? String(item.lineItemKey).toLowerCase()
        : "";
      const itemSku = item.sku ? String(item.sku).toLowerCase() : "";
      for (const d of listDesigns) {
        const fileId = d.file_id;
        if (!fileId || assignedFileIds.has(fileId)) continue;
        const dLine = (d.line_item_key || d.lineItemKey || "")
          .toString()
          .toLowerCase();
        const dSku = (d.design_sku || d.sku || "").toString().toLowerCase();
        if (dLine && itemLine && dLine === itemLine) {
          byItem[idx].push(d);
          assignedFileIds.add(fileId);
        } else if (
          dSku &&
          itemSku &&
          dSku === itemSku &&
          skuCounts[itemSku] === 1
        ) {
          byItem[idx].push(d);
          assignedFileIds.add(fileId);
        }
      }
    }

    // 3) unique sanitized name match
    for (let idx = 0; idx < items.length; idx++) {
      const item = items[idx];
      const itemSan = sanitizeText(item.name || "");
      if (!itemSan || (nameCounts[itemSan] || 0) !== 1) continue;
      for (const d of listDesigns) {
        const fileId = d.file_id;
        if (!fileId || assignedFileIds.has(fileId)) continue;
        const fn = (d.file_name || "").toLowerCase();
        if (fn.includes(itemSan)) {
          byItem[idx].push(d);
          assignedFileIds.add(fileId);
        }
      }
    }

    // return mapping
    return byItem;
  }, [designs, order]);

  const handleSaveNote = async () => {
    if (!order?.orderId || !order?.orderNumber) {
      toast("Invalid order data", "error");
      return;
    }

    setSavingNote(true);

    try {
      const resp = await axios.post(
        `${settings.apiURL}/embroidery_update_order_note.php`,
        {
          orderId: order.orderId,
          orderNumber: order.orderNumber,
          internalNote: internalNote.trim(),
        },
        { headers: authHeader() }
      );

      if (resp.data.success) {
        toast("Internal note updated successfully", "success");
        setOrder((prev) => ({ ...prev, internalNotes: internalNote.trim() }));
      } else {
        toast(resp.data.message || "Failed to update note", "error");
      }
    } catch (err) {
      console.error("Note update error:", err);
      toast("Failed to update note", "error");
    } finally {
      setSavingNote(false);
    }
  };

  const handleCreateLabel = async () => {
    if (!order?.orderId || !order?.orderNumber) {
      toast("Invalid order data", "error");
      return;
    }

    if (order.label_created) {
      toast("Label already created for this order", "info");
      return;
    }

    if (!order.design_uploaded) {
      toast("Please upload design first", "warning");
      return;
    }

    setCreatingLabel(true);

    try {
      let resp;

      if (order.source === "maya") {
        resp = await axios.post(
          `${settings.apiURL}/create_shipstation_label_maya.php`,
          {
            orderId: order.orderId,
            orderNumber: order.orderNumber,
          },
          { headers: authHeader() }
        );
      } else if (order.source === "customzon") {
        resp = await axios.post(
          `${settings.apiURL}/create_shipstation_label_customzon_proxy.php`,
          {
            orderId: order.orderId,
            orderNumber: order.orderNumber,
          },
          { headers: authHeader() }
        );
      }

      if (resp.data.success) {
        const isTestMode = resp.data.test_mode === true;
        const successMessage = isTestMode
          ? "✅ TEST LABEL created successfully! (No charges applied)"
          : "✅ Label created successfully!";

        toast(successMessage, "success");

        if (resp.data.labelDownloadUrl) {
          window.open(resp.data.labelDownloadUrl, "_blank");
        }

        setOrder((prev) => ({
          ...prev,
          label_created: true,
          label_file_path: resp.data.label_file_path,
          tracking_number: resp.data.tracking_number,
          labelDownloadUrl: resp.data.labelDownloadUrl,
        }));
      } else {
        toast(resp.data.message || "Label creation failed", "error");
      }
    } catch (err) {
      console.error("Label creation error:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Label creation failed. Please try again.";
      toast(errorMessage, "error");
    } finally {
      setCreatingLabel(false);
    }
  };

  const handleDownloadLabel = () => {
    if (!order?.labelDownloadUrl) {
      toast("Label URL not found", "error");
      return;
    }
    window.open(order.labelDownloadUrl, "_blank");
  };

  const handleProductionStatusChange = async (checked) => {
    if (!order?.orderId || !order?.orderNumber) {
      toast("Invalid order data", "error");
      return;
    }

    const newStatus = checked ? "in_progress" : "pending";
    setUpdatingStatus(true);

    try {
      const resp = await axios.post(
        `${settings.apiURL}/embroidery_update_production_status.php`,
        {
          orderId: order.orderId,
          orderNumber: order.orderNumber,
          productionStatus: newStatus,
          source: order.source || "maya",
        },
        { headers: authHeader() }
      );

      if (resp.data.success) {
        toast(`Production status updated to ${newStatus}`, "success");
        setOrder((prev) => ({ ...prev, production_status: newStatus }));
      } else {
        toast(resp.data.message || "Failed to update status", "error");
      }
    } catch (err) {
      console.error("Status update error:", err);
      toast("Failed to update status", "error");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Image hover handlers
  const onImageMouseEnter = (e, src) => {
    if (!src) return;
    setHoverPreview({
      visible: true,
      src,
      x: e.clientX,
      y: e.clientY,
    });
  };

  const onImageMouseMove = (e) => {
    if (!hoverPreview.visible) return;
    setHoverPreview((prev) => ({
      ...prev,
      x: e.clientX,
      y: e.clientY,
    }));
  };

  const onImageMouseLeave = () => {
    setHoverPreview({
      visible: false,
      src: null,
      x: 0,
      y: 0,
    });
  };

  if (loading) return <div style={{ padding: 20 }}>Loading...</div>;
  if (!order) return <div style={{ padding: 20 }}>Order not found.</div>;

  const items = filteredItems(order);

  return (
    <div style={{ minHeight: "100vh", background: "#f7f7f7", padding: "16px" }}>
      {hoverPreview.visible && hoverPreview.src && (
        <div
          style={{
            position: "fixed",
            left: Math.min(
              window.innerWidth - hoverPreviewSize - 20,
              hoverPreview.x + 20
            ),
            top: Math.min(
              window.innerHeight - hoverPreviewSize - 20,
              hoverPreview.y + 20
            ),
            width: hoverPreviewSize,
            height: hoverPreviewSize,
            zIndex: 2000,
            border: "1px solid rgba(0,0,0,0.12)",
            background: "#fff",
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 8,
            borderRadius: 6,
            overflow: "hidden",
          }}
        >
          <img
            src={hoverPreview.src}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: "block",
            }}
            draggable={false}
          />
        </div>
      )}

      <div style={{ textAlign: "center", marginBottom: "16px" }}>
        <button
          onClick={logout}
          style={{
            background: "#ff7043",
            color: "#fff",
            border: "none",
            padding: "10px 18px",
            borderRadius: "6px",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Logout
        </button>
      </div>

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          background: "#fff",
          borderRadius: "10px",
          padding: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
          }}
        >
          <button
            className="btn btn-link"
            onClick={() => navigate("/home")}
            style={{ textDecoration: "none" }}
          >
            ⬅️ Back to Workspace
          </button>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!prevOrderId}
              onClick={() =>
                prevOrderId &&
                navigate(`/embroidery-order/${prevOrderId}`, {
                  state: { orders: ordersList },
                })
              }
            >
              Prev
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              disabled={!nextOrderId}
              onClick={() =>
                nextOrderId &&
                navigate(`/embroidery-order/${nextOrderId}`, {
                  state: { orders: ordersList },
                })
              }
            >
              Next
            </button>
          </div>
        </div>

        <div style={{ marginBottom: "12px" }}>
          <span
            style={{
              backgroundColor: order.source === "maya" ? "#007bff" : "#28a745",
              color: "white",
              padding: "4px 12px",
              borderRadius: "4px",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {order.source === "maya" ? "Maya Order" : "Customzon Order"}
          </span>
        </div>

        <h4>
          Order #{order.orderNumber} — {order.shipTo?.name}
        </h4>
        <p style={{ marginBottom: "8px" }}>
          Created:{" "}
          {order.createDate ? new Date(order.createDate).toLocaleString() : "-"}
        </p>

        {/* Label Status & Actions */}
        <div
          style={{
            marginBottom: "20px",
            padding: "12px",
            border: "1px solid #dee2e6",
            borderRadius: "6px",
            backgroundColor: order.label_created ? "#d4edda" : "#f8f9fa",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <strong>Label Status: </strong>
              {order.label_created ? (
                <span style={{ color: "#28a745", fontWeight: "bold" }}>
                  ✅ Label Created
                </span>
              ) : (
                <span style={{ color: "#856404", fontWeight: "bold" }}>
                  ⏳ No Label Yet
                </span>
              )}
              {order.tracking_number && (
                <div style={{ marginTop: "4px", fontSize: "13px" }}>
                  Tracking: <strong>{order.tracking_number}</strong>
                </div>
              )}
            </div>
            <div>
              {order.label_created ? (
                <button
                  className="btn btn-primary"
                  onClick={handleDownloadLabel}
                >
                  📥 Download Label
                </button>
              ) : (
                <button
                  className="btn btn-warning"
                  onClick={handleCreateLabel}
                  disabled={creatingLabel || !order.design_uploaded}
                  title={
                    !order.design_uploaded ? "Please upload design first" : ""
                  }
                >
                  {creatingLabel ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Creating Label...
                    </>
                  ) : (
                    "Create Label"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Production Status - Only for embroidery users */}
        {isEmbroideryUser && (
          <div
            style={{
              marginBottom: "20px",
              padding: "12px",
              border: "1px solid #dee2e6",
              borderRadius: "6px",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <label
                style={{
                  fontWeight: "bold",
                  marginBottom: 0,
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={order.production_status === "in_progress"}
                  onChange={(e) =>
                    handleProductionStatusChange(e.target.checked)
                  }
                  disabled={updatingStatus}
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "8px",
                    cursor: "pointer",
                  }}
                />
                <span>Mark as In Progress</span>
              </label>
              <span
                style={{
                  padding: "4px 12px",
                  borderRadius: "4px",
                  fontSize: "13px",
                  fontWeight: "bold",
                  backgroundColor:
                    order.production_status === "in_progress"
                      ? "#ffc107"
                      : "#6c757d",
                  color: "#fff",
                }}
              >
                {order.production_status === "in_progress"
                  ? "IN PROGRESS"
                  : "PENDING"}
              </span>
              {updatingStatus && (
                <span className="spinner-border spinner-border-sm"></span>
              )}
            </div>
          </div>
        )}

        {/* Internal Notes */}
        <div
          style={{
            marginBottom: "20px",
            padding: "12px",
            border: "1px solid #dee2e6",
            borderRadius: "6px",
            backgroundColor: "#f8f9fa",
          }}
        >
          <label
            style={{
              fontWeight: "bold",
              marginBottom: "8px",
              display: "block",
            }}
          >
            Internal Notes
          </label>
          <div
            style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
          >
            <textarea
              className="form-control"
              rows="3"
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              placeholder="Add notes about this order..."
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-primary"
              onClick={handleSaveNote}
              disabled={savingNote}
              style={{ minWidth: "80px" }}
            >
              {savingNote ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>

        {/* Order-level design (single .ai) */}
        {orderLevelDesigns && orderLevelDesigns.length > 0 && (
          <div
            style={{
              marginBottom: 12,
              padding: 8,
              border: "1px solid #eee",
              borderRadius: 6,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Order Design</div>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {orderLevelDesigns.map((d, i) => (
                <div
                  key={i}
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  <strong>{truncate(d.file_name || "", 60)}</strong>
                  <span style={{ color: "#666" }}>
                    • {formatFileSizeFriendly(d.file_size)}
                  </span>
                  {isEmbroideryUser && (
                    <a
                      className="btn btn-sm btn-outline-primary"
                      href={`https://drive.google.com/uc?id=${d.file_id}&export=download`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ marginLeft: 8 }}
                    >
                      📥 Download
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items Table */}
        <h5>Order Items & Designs</h5>
        <div style={{ overflowX: "auto" }}>
          <table className="table table-sm table-bordered">
            <thead>
              <tr>
                <th>#</th>
                <th>Image</th>
                <th>Item</th>
                <th>SKU</th>
                <th>Color</th>
                <th>Size</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const color =
                  getOptionValue(item, ["color", "colors"]) ||
                  item.color ||
                  "N/A";
                const size =
                  getOptionValue(item, ["size", "sizes"]) || item.size || "N/A";
                const personalization =
                  getOptionValue(item, [
                    "personalization",
                    "personalisation",
                    "customization",
                  ]) || null;

                // Use backend-provided uploaded_designs if present; otherwise use computed mapping
                const computed = mappedDesignsByItem[idx] || [];
                const uploaded =
                  item.uploaded_designs && item.uploaded_designs.length > 0
                    ? item.uploaded_designs
                    : computed;

                return (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td
                      onMouseEnter={(e) => onImageMouseEnter(e, item.imageUrl)}
                      onMouseMove={onImageMouseMove}
                      onMouseLeave={onImageMouseLeave}
                      style={{ width: 80, textAlign: "center" }}
                    >
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            cursor: "zoom-in",
                            borderRadius: 4,
                          }}
                          draggable={false}
                        />
                      ) : (
                        <div
                          style={{
                            width: "50px",
                            height: "50px",
                            backgroundColor: "#e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "10px",
                          }}
                        >
                          No Image
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        maxWidth: 260,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={item.name}
                    >
                      {item.name}
                      {personalization && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#666",
                            marginTop: "6px",
                          }}
                        >
                          <strong>Personalization:</strong> {personalization}
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        maxWidth: 120,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.sku || "-"}
                    </td>
                    <td>{color}</td>
                    <td>{size}</td>
                    <td>{item.quantity}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmbroideryOrderDetailsPage;
